// MF-67 — Roster picker for journal entries. Async-searches the user roster by code or last name,
// lets staff add one or more students, and assigns each a role (involved / witness / perpetrator /
// victim / recipient). Encapsulates the whole "students involved" section; the parent just holds the
// resulting array of { userId, code, name, yearLevel, house, formGroup, role }.
import { useEffect, useMemo, useRef, useState } from 'react'
import { Autocomplete, TextField, MenuItem, IconButton, Tooltip } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { inputSx } from './formStyles'
import { getUsersSkipLimit } from '../../../../services/user/user.service'

const STUDENT_ROLES = ['involved', 'witness', 'perpetrator', 'victim', 'recipient']

// Normalises the various possible list-response shapes into a plain array of user docs.
const rowsFrom = (body) => {
  const data = body?.data ?? body
  if (Array.isArray(data)) return data
  return data?.paginatedResults ?? data?.users ?? data?.list ?? []
}

const userToStudent = (u) => ({
  userId: u._id,
  code: u.code || '',
  name: [u.firstName, u.lastName].filter(Boolean).join(' ').trim(),
  yearLevel: u.data?.yearLevel ?? '',
  house: u.data?.house ?? '',
  formGroup: u.data?.form ?? '',
  role: 'involved'
})

const optionLabel = (u) => {
  const name = [u.lastName?.toUpperCase(), u.firstName].filter(Boolean).join(', ')
  return `${name || u.firstName || '—'}${u.code ? ` (${u.code})` : ''}`
}

const StudentPicker = ({ value = [], onChange }) => {
  const [input, setInput] = useState('')
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef(null)

  const selectedIds = useMemo(() => new Set(value.map((s) => String(s.userId))), [value])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const term = input.trim()
    if (term.length < 2) {
      setOptions([])
      return
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        // No single OR-search field on the roster endpoint, so query code + last name and merge.
        const [byCode, byLast] = await Promise.all([
          getUsersSkipLimit(0, 15, { code: term }).catch(() => null),
          getUsersSkipLimit(0, 15, { lastName: term }).catch(() => null)
        ])
        const merged = [...rowsFrom(byCode), ...rowsFrom(byLast)]
        const unique = Object.values(
          merged.reduce((acc, u) => {
            if (u?._id) acc[String(u._id)] = u
            return acc
          }, {})
        )
        setOptions(unique)
      } finally {
        setLoading(false)
      }
    }, 300)
    return () => debounceRef.current && clearTimeout(debounceRef.current)
  }, [input])

  const addStudent = (user) => {
    if (!user || selectedIds.has(String(user._id))) return
    onChange([...value, userToStudent(user)])
  }

  const setRole = (i, role) => onChange(value.map((s, si) => (si === i ? { ...s, role } : s)))
  const removeStudent = (i) => onChange(value.filter((_, si) => si !== i))

  return (
    <div className='flex flex-col' style={{ gap: 10 }}>
      <Autocomplete
        size='small'
        loading={loading}
        options={options}
        filterOptions={(x) => x}
        getOptionLabel={optionLabel}
        getOptionDisabled={(o) => selectedIds.has(String(o._id))}
        inputValue={input}
        onInputChange={(_, v) => setInput(v)}
        value={null}
        blurOnSelect
        clearOnBlur
        onChange={(_, user) => {
          addStudent(user)
          setInput('')
        }}
        noOptionsText={input.trim().length < 2 ? 'Type a name or code…' : 'No students found'}
        renderInput={(params) => (
          <TextField {...params} placeholder='Search by surname or student code…' sx={inputSx} />
        )}
      />

      {value.length === 0 ? (
        <div
          className='text-center'
          style={{
            padding: 16,
            borderRadius: 10,
            border: '1px dashed rgb(203, 213, 225)',
            color: 'rgb(100, 116, 139)',
            fontSize: 13
          }}
        >
          No students added yet
        </div>
      ) : (
        <div className='flex flex-col' style={{ gap: 8 }}>
          {value.map((s, i) => (
            <div
              key={s.userId || i}
              className='flex items-center'
              style={{
                gap: 12,
                padding: '8px 8px 8px 14px',
                borderRadius: 10,
                border: '1px solid rgb(226, 232, 240)',
                background: '#fff'
              }}
            >
              <div className='flex flex-col grow' style={{ minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: 'rgb(15, 23, 42)' }}>{s.name}</span>
                {s.code ? <span style={{ fontSize: 12, color: 'rgb(100, 116, 139)' }}>{s.code}</span> : null}
              </div>
              <TextField
                select
                size='small'
                value={s.role}
                onChange={(e) => setRole(i, e.target.value)}
                SelectProps={{ SelectDisplayProps: { 'aria-label': `Role of ${s.name}` } }}
                sx={{ ...inputSx, width: 150 }}
              >
                {STUDENT_ROLES.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
              <Tooltip arrow title='Remove student'>
                <IconButton aria-label={`Remove ${s.name}`} size='small' onClick={() => removeStudent(i)}>
                  <FontAwesomeIcon icon={['fal', 'trash-alt']} style={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default StudentPicker
